<template>
  <div class="media__image">
    <!--v-loading="loadingPreview"-->
    <div class="media__image--wrapper" v-if="previewUrl" >
      <span class="media__image-preview" v-if="fullUrl"><i class="el-icon-zoom-in"></i></span>
      <img
        v-if="previewUrl && isImage"
        :src="previewUrl"
        :alt="title"
        class="preview"
        @load="previewLoadSuccess"
        @error="previewLoadError"
        @click="zoom"
      />
      <a v-if="isPDF" class="hidden-lg-and-up" target="_blank" :href="fullUrl" >
        <img
          :src="previewUrl"
          :alt="title"
        />
      </a>
      <img v-if="isPDF" class="preview hidden-md-and-down"
        :src="previewUrl"
        :alt="title"
        @load="previewLoadSuccess"
        @error="previewLoadError"
        @click="zoom"
      />      
      <img
        v-if="isEmbeddedVideo"
        :src="previewUrl"
        :alt="title"
        class="preview embedded"
        @load="previewLoadSuccess"
        @error="previewLoadError"
        @click="zoom"
      />
    </div>
    <span :class="{ 'media__image--title': true, 'embedded': isEmbeddedVideo }">{{ title }}</span>

    <el-dialog
      :custom-class="dialogCustomClass"
      :append-to-body="true"
      :title="title"
      :visible.sync="showZoom"
    >
      <div class="dialog">
        <inline-pdf
          v-if="isPDF"
          class="inline-pdf"
          :url="fullUrl"
          :fallbackMessage="fallbackMessage"
        />
        <img
          v-if="isImage"
          class="full"
          :src="fullUrl"
          :alt="title"
        />
        <div
          v-if="isEmbeddedVideo && showZoom"
          v-html="resizedEmbeddedVideoHTML" />
        <span v-if="summary" class="summary">{{ summary }}</span>
      </div>
    </el-dialog>
  </div>
</template>

<script>
  import {mapState} from 'vuex';
  import offline from '@/offline';
  import InlinePdf from './InlinePdf';
  import get from 'lodash.get';
  import i18n from '@/configured/vue-i18n/i18n';
  import fallbackJpg from '@/assets/images/fallbackSquare.jpg';
  import fallbackPdf from '@/assets/images/fallbackPdf.jpg';
  import store from '@/store';
  import products from '@/components/products';

  export default {
    name: 'MediaImage',
    data() {
      return {
        showZoom: false,
        loadingPreview: true,
        loadingPreviewError: false,
        fallbackJpg,
        fallbackPdf,
        embeddedVideoHTML: '',
        embeddedVideoImage: '',
        embeddedVideoAspectRatio: 1,
      }
    },
    props: {
      image: {
        type: Object
      }
    },
    mounted () {
      this.loadEmbeddedVideoMetadata();
    },
    computed: {
      ...mapState(offline.store.namespace, [
        'isOnline'
      ]),
      previewUrl,
      fullUrl,
      title() { return get(this.image, 'title') },
      summary() { return get(this.image, 'summary') },
      isImage() { return get(this.image, 'kind') === 'image' },
      isPDF() { return get(this.image, 'kind') === 'pdf' },
      isEmbeddedVideo() { return get(this.image, 'kind') === 'json' },
      fallbackMessage,
      // For some reason the v-bind on the :custom-class was failing, this is a workaround
      dialogCustomClass() { return this.isPDF ? 'media--pdf': '' },
      resizedEmbeddedVideoHTML,
    },
    methods: {
      zoom,
      previewLoadSuccess,
      previewLoadError,
      loadEmbeddedVideoMetadata,
    },
    components: {
      InlinePdf,
    }

  }

  function zoom() {
    if (this.fullUrl || this.embeddedVideoHTML) {
      this.showZoom = true;
    }
  }

  function fullUrl() {
    const url = get(this.image, 'url');
    if (!url || !this.isOnline) {
      return false;
    }
    return url;
  }

  function previewUrl() {
    if (this.isImage) {
      if (this.loadingPreview || this.loadingPreviewError) {
        return this.fallbackJpg;
      }

      let thumb = get(this.image, 'thumbnail[0].url');
      if (thumb) {
        return thumb;
      }

      let square = get(this.image, 'mediaSquareUrl');
      if (square) {
        return square;
      }
    }

    if (this.isPDF) {
      if (this.loadingPreview || this.loadingPreviewError) {
        return this.fallbackPdf;
      }

      let thumb = get(this.image, 'thumbnail[0].url');
      if (thumb) {
        return thumb;
      }

      let square = get(this.image, 'mediaSquareUrl');
      if (square) {
        return square;
      }
      return this.fallbackPdf;
    }

    if (this.isEmbeddedVideo) {
      return (this.loadingPreview) ? this.fallbackJpg : this.embeddedVideoImage;
    }
    return null;
  }

  function loadEmbeddedVideoMetadata() {
    if (!this.isEmbeddedVideo) {
      this.previewLoadSuccess();
      return;
    }
    store.dispatch(`${products.store.namespace}/loadEmbeddedVideoMetadata`, this.image).then((data) => {
      this.embeddedVideoImage = data.images[1].url;
      this.embeddedVideoHTML = data.code;
      this.embeddedVideoAspectRatio = data.aspectRatio / 100;
    });
  }

  function resizedEmbeddedVideoHTML() {
    // Fix the width/height bug: it always comes in at 1920x1080
    // This is a workaround for the clientWidth being 0 when we have to make this calc.
    const windowWidth = window.innerWidth;
    const dialogRatio = (windowWidth < 768) ? .8 : .6;
    const width = Math.floor(dialogRatio * windowWidth);
    if (width > 0) {
      const height = Math.floor(width * this.embeddedVideoAspectRatio);
      return this.embeddedVideoHTML.
          replace('width="1920"', `width="${width}"`).
          replace('height="1080"', `height="${height}"`);
    }
    return this.embeddedVideoHTML;
  }

  function previewLoadSuccess() {
    this.loadingPreview = false;
  }

  function previewLoadError() {
    this.loadingPreview = false;
    this.loadingPreviewError = true;
  }

  function fallbackMessage() {
    return i18n.t('inlinePdfFallback')
      + ` <a href="${this.fullUrl}">${this.title}</a>`;
  }
</script>

<style lang="scss" scoped>

  .media__image {
    position: relative;

    &--wrapper {
      overflow: hidden;
      display: block;
      width: 100%;
      padding-top: 75%;
      margin-bottom: 20px;

      img {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        -o-object-fit: cover;
        object-fit: cover;

        &.preview {
          cursor: pointer;
        }
        &.embedded {
          -o-object-fit: contain;
          object-fit: contain;
        }
      }
    }

    &--title {
      position: absolute;
      font-style: italic;
      bottom: -22px;
      max-height: 24px;
      overflow: hidden;

      &.embedded {
        max-height: unset;
      }
    }

    &-preview {
      z-index: 2;
      display: block;
      top: 0;
      left: 0;
      position: absolute;
      width: 30px;
      height: 30px;
      background-color: #ED1C2E;
      text-align: center;
      line-height: 30px;

      i {
        color: #ffffff;

        &:before {
          font-weight: bold;
        }
      }
    }

    .dialog object {
      position: absolute;
      width: 100%;
      height: 100%;
    }
  }

  .inline-pdf {
    min-height: 61vh;
  }

  .el-dialog {
    height: 70vh;
  }

</style>

